import React from 'react';
import { useEffect, useState } from 'react';
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Box,} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useFetch } from '../../utils/FetchTemplate';
import { FormField } from '../../interfaces/User.interface';
import { useSetting } from '../../contexts/SettingContext';
import { IMember } from '../../interfaces/Member.interface';
import { Autocomplete } from '@mui/material';
import { IPayment } from '../../interfaces/StudentPayment.interface';

//声明接口
interface AddStudentDialogProps {
  open: boolean;
  serviceId: string;
  mode?: 'add' | 'edit';
  onClose: () => void;
  onSubmit: (data: any) => void;
  initialData?: {
    member: IMember;
    payment: IPayment;
  };
}

const AddStudentDialog: React.FC<AddStudentDialogProps> = ({ open, serviceId, mode = 'add', onClose, onSubmit, initialData }) => {
  const { settings, fetchSettings } = useSetting();
  
  const {control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm();
  const discountTypeValue = watch('discountType');

  const [availableStudents, setAvailableStudents] = useState<any[]>([]);
  const { postData, getData, typedPostData } = useFetch();

  const getAvailableStudents = async () => {
    if (!serviceId || !settings.userId || mode == 'edit') return; // VIP handled by parent
    try {
      const data = await typedPostData<{ serviceId: string; userId: string }, IMember[]>('/api/setting/availableStudents',{ serviceId, userId: settings.userId });
      setAvailableStudents(data);
    } catch (err) {
      console.error('Failed to get students', err);
    }
  };

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        setValue('student', initialData.member);
        setValue('discountType', initialData.payment.discountType);
        setValue('discountValue', initialData.payment.discountAmount ?? 0);
      } else {
        getAvailableStudents();
      }
    }
  }, [open, serviceId, settings.userId, initialData, mode, setValue]);

  useEffect(() => {
    if (discountTypeValue === 'none') {
      setValue('discountValue', 0);
    }
  }, [discountTypeValue, setValue]);

  const formFields: FormField[] = [
    {
      name: 'student',
      question: 'Student',
      validation: 'object',
      required: true,
      questionType: 'autocomplete',
      data: availableStudents,
      props: {
        getOptionLabel: (option: any) => `${option.firstName} ${option.lastName}`,
        isOptionEqualToValue: (option: any, value: any) => option.memberId === value.memberId,
        disabled: mode === 'edit',
      },
    },
    {
      name: 'discountType',
      question: 'Discount Type',
      validation: 'text',
      required: false,
      questionType: 'autocomplete',
      data: ['none', 'percent', 'fixed'],
      props: {
        getOptionLabel: (option: any) =>
          option === 'percent' ? 'Percentage' :
          option === 'fixed' ? 'Fixed Value' :
          'None',
      }
    },
    {
      name: 'discountValue',
      question: 'Discount Value',
      validation: 'number',
      required: false,
      questionType: 'number',
    },
  ];

  const renderField = (field: FormField) => {
    const commonProps = {name: field.name, control,rules: field.required ? { required: `${field.question} is required` } : {},};

    switch (field.questionType) {
        case 'number':
          const isDisabled = discountTypeValue === 'none';
          return (
            <Controller
                key={field.name}
                {...commonProps}
                rules={{
                  min: {
                    value: discountTypeValue === 'none' ? 0 : 0.01, 
                    message: `${field.question} must be greater than 0`,
                  },
                }}
                render={({ field: controllerField }) => (
                  <TextField
                    {...controllerField}
                    type="number"
                    label={field.question}
                    fullWidth
                    disabled={isDisabled}
                    value={isDisabled ? 0 : controllerField.value ?? ''} 
                    error={!!errors[field.name]}
                    helperText={(errors[field.name]?.message as string) || ''}
                    sx={{ mt: 2 }}
                  />
                )}
            />
          );
            
            case 'autocomplete':
              return (
                <Controller
                  key={field.name}
                  name={field.name}
                  control={control}
                  rules={field.required ? { required: `${field.question} is required` } : {}}
                  render={({ field: controllerField }) => (
                    <Autocomplete
                      options={field.data || []}
                      getOptionLabel={field.props?.getOptionLabel || ((option: any) => String(option))}
                      isOptionEqualToValue={field.props?.isOptionEqualToValue || ((a: any, b: any) => a === b)}
                      value={controllerField.value || null}
                      onChange={(_, value) => controllerField.onChange(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={field.question}
                          fullWidth
                          error={!!errors[field.name]}
                          helperText={(errors[field.name]?.message as string) || ''}
                          sx={{ mt: 2 }}
                        />
                      )}
                      disabled={field.props?.disabled}
                    />
                  )}
                />
              );
        default:
            return null;
        }
    };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === 'edit' ? 'Edit Student' : 'Add Student'}</DialogTitle>
      <form onSubmit={handleSubmit(async (data) => {
        try {
          const paymentData: IPayment = initialData?.payment? {
            ...initialData.payment,
            discountType: data.discountType,
            discountAmount: Number(data.discountValue || 0),
          }
          : {
            paymentId: '',
            serviceId,
            memberId: data.student.memberId,
            discountType: data.discountType,
            discountAmount: Number(data.discountValue || 0),
          } as Omit<IPayment, 'createdAt'> as IPayment;

          let url = ''
          let payload: any;

          switch(mode) {
            case 'add':
              url = '/api/setting/addStudentToService';
              payload = {
                userId: settings.userId,
                data: paymentData,
              };
              break;
            case 'edit':
              url = '/api/payment/updateStudentPayment';
              payload = paymentData;
              break;
            default:
              throw new Error('Invalid mode');
          };
          await postData(url, payload);

          onSubmit(data.student);
          handleClose();
        } catch (err) {
          console.error('Failed to add student:', err);
        }
      })}>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column">
            {formFields.map((field) => renderField(field))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">Add</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddStudentDialog;